import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import type React from 'react';
import AuthLayout from '@/components/AuthLayout';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import type { AuthUser, StaffMember } from '@/types';

const OTP_LENGTH = 6;
const RESEND_SECONDS = 30;

interface VerifyOtpResponse {
    accessToken: string;
    staff: StaffMember;
}

const ROLE_ROUTES: Record<string, string> = {
    'Admin': '/dashboard',
    'Case Manager': '/case-manager',
    'Mediator': '/mediator',
};

export default function VerifyOTP() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const identifier = (location.state as { identifier?: string })?.identifier ?? '';

    const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [countdown, setCountdown] = useState(RESEND_SECONDS);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const isComplete = useMemo(() => otp.every((d) => d !== ''), [otp]);

    useEffect(() => {
        inputRefs.current[0]?.focus();
    }, []);

    useEffect(() => {
        if (!identifier) navigate('/login', { replace: true });
    }, [identifier, navigate]);

    useEffect(() => {
        if (countdown <= 0) return;
        const timer = setInterval(() => setCountdown((c) => c - 1), 1000);
        return () => clearInterval(timer);
    }, [countdown]);

    const handleChange = useCallback(
        (index: number, value: string) => {
            if (!/^\d*$/.test(value)) return;
            const digit = value.slice(-1);
            setOtp((prev) => {
                const next = [...prev];
                next[index] = digit;
                return next;
            });
            if (error) setError('');
            if (digit && index < OTP_LENGTH - 1) {
                inputRefs.current[index + 1]?.focus();
            }
        },
        [error],
    );

    const handleKeyDown = useCallback(
        (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Backspace' && !otp[index] && index > 0) {
                inputRefs.current[index - 1]?.focus();
            }
        },
        [otp],
    );

    const handlePaste = useCallback((e: React.ClipboardEvent) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
        if (!pasted) return;
        setOtp((prev) => {
            const next = [...prev];
            pasted.split('').forEach((ch, i) => {
                next[i] = ch;
            });
            return next;
        });
        const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
        inputRefs.current[focusIdx]?.focus();
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isComplete) return;
        setIsLoading(true);
        setError('');

        try {
            const res = await api.post<VerifyOtpResponse>('/api/auth/staff/verify-otp', {
                email: identifier,
                otp: otp.join(''),
            });

            const { accessToken, staff } = res.response.data!;

            const user: AuthUser = {
                id: staff.id,
                name: staff.name,
                email: staff.email,
                role: staff.role,
                userType: 'staff',
            };

            login(accessToken, user);

            const route = ROLE_ROUTES[staff.role] ?? '/dashboard';
            navigate(route, { replace: true });
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Invalid OTP. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = async () => {
        if (countdown > 0) return;
        setCountdown(RESEND_SECONDS);
        try {
            await api.post('/api/auth/staff/login', { email: identifier });
        } catch {
            // silently ignore — OTP timer already reset
        }
    };

    const formatTime = (s: number) => {
        const mins = Math.floor(s / 60).toString().padStart(2, '0');
        const secs = (s % 60).toString().padStart(2, '0');
        return `${mins}:${secs}`;
    };

    return (
        <AuthLayout>
            <h2 className="mt-10 text-[20px] font-bold text-secondary sm:text-[22px]">Verify Your Account</h2>

            <p className="mt-2 text-[15px] leading-relaxed text-[#888]">
                Type the 6-digit code sent to <span className="font-medium text-secondary">{identifier}</span>. This
                helps keep your account secure.
            </p>

            <form onSubmit={handleSubmit} className="mt-8">
                <div className="flex gap-2 sm:gap-3" onPaste={handlePaste}>
                    {otp.map((digit, i) => (
                        <input
                            key={i}
                            ref={(el) => {
                                inputRefs.current[i] = el;
                            }}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            title={`Digit ${i + 1}`}
                            onChange={(e) => handleChange(i, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(i, e)}
                            onFocus={(e) => e.target.select()}
                            className="aspect-square min-w-0 flex-1 rounded-lg border border-[#D2D2D2] bg-white text-center text-lg font-bold text-secondary outline-none transition-colors focus:border-secondary focus:ring-2 focus:ring-secondary sm:max-w-14"
                        />
                    ))}
                </div>

                {error && <p className="mt-3 text-sm text-red-500">{error}</p>}

                <button
                    type="submit"
                    disabled={isLoading || !isComplete}
                    className="mt-8 flex w-full items-center justify-center rounded-lg bg-secondary py-3.5 text-[15px] font-bold text-white transition-all hover:bg-secondary-400 disabled:opacity-60"
                >
                    {isLoading ? (
                        <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                    ) : (
                        'Verify Account'
                    )}
                </button>

                <p className="mt-4 text-center text-[14px] text-[#888]">
                    Didn&apos;t get the code?{' '}
                    {countdown > 0 ? (
                        <span className="font-medium text-primary">Resend OTP in {formatTime(countdown)}</span>
                    ) : (
                        <button type="button" onClick={handleResend} className="font-medium text-primary hover:underline">
                            Resend OTP
                        </button>
                    )}
                </p>
            </form>
        </AuthLayout>
    );
}
